import { AddDays, DaysBetween, ToIsoDate } from '../../utils/dateUtils';
import { SumWindow } from './SumWindow';

/**
 * Crescimento %: (currentValue - pastValue) / pastValue * 100.
 * Retorna 0 quando past = 0 e current = 0; retorna 100 (cap) quando past = 0 e
 * current > 0 (∞ visualizado como 100% pra display ficar legível).
 */
function pctChange(current, past) {
    const c = Number(current || 0);
    const p = Number(past || 0);
    if (p === 0) {
        if (c === 0) return 0;
        // Cap em ±999 pra display não estourar (display formata como int %).
        return c > 0 ? 999 : -999;
    }
    return ((c - p) / p) * 100;
}

/**
 * Crescimento semanal = comparação dos últimos 7d contra 8-14d atrás.
 * Lê `row.sales7` e `row.week2` (já pre-computados pelo Selector).
 */
export function WeekGrowth(row) {
    return pctChange(row?.sales7, row?.week2);
}

/**
 * Variação mensal = comparação dos últimos 30d contra 31-60d atrás.
 * Lê `row.sales30` e `row.month2`.
 */
export function MonthVariation(row) {
    return pctChange(row?.sales30, row?.month2);
}

/**
 * Split do período ATIVO: divide o range [periodStart..periodEnd] em duas
 * metades de tamanho igual (segunda metade pode ter 1 dia a mais quando o
 * total é ímpar), soma cada uma a partir de `row.dailyByDate`, e devolve a
 * variação % da segunda metade em relação à primeira.
 *
 * Use case: usuário filtra 30d, vê instantaneamente se a SEGUNDA quinzena
 * cresceu/caiu vs a primeira — sem precisar fatiar dois ranges separados.
 */
export function PeriodSplit(row, ctx) {
    if (!ctx?.periodStartIso || !ctx?.periodEndIso || !row?.dailyByDate) return 0;
    const start = ctx.periodStartIso;
    const end = ctx.periodEndIso;
    const days = DaysBetween(start, end);
    if (days < 2) return 0;
    const halfFloor = Math.floor(days / 2);
    const startDate = new Date(`${start}T00:00:00Z`);
    const firstHalfEndDate = AddDays(startDate, halfFloor - 1);
    const secondHalfStartDate = AddDays(startDate, halfFloor);
    const firstHalfEnd = ToIsoDate(firstHalfEndDate);
    const secondHalfStart = ToIsoDate(secondHalfStartDate);
    const firstSum = SumWindow(row.dailyByDate, start, firstHalfEnd);
    const secondSum = SumWindow(row.dailyByDate, secondHalfStart, end);
    return pctChange(secondSum, firstSum);
}

/**
 * Total de pedidos no período (paid + cancelled + other) — lê `row.orderTotals`.
 * orderTotals é populado pelo backend (`OperacoesService._loadSnapshot`).
 */
export function OrderCount(row) {
    const t = row?.orderTotals || { paid: 0, cancelled: 0, other: 0 };
    return Number(t.paid || 0) + Number(t.cancelled || 0) + Number(t.other || 0);
}

export function PaidCount(row) {
    return Number(row?.orderTotals?.paid || 0);
}

export function CancelledCount(row) {
    return Number(row?.orderTotals?.cancelled || 0);
}

/** % cancelamento = cancelled / total * 100. */
export function CancelPercent(row) {
    const total = OrderCount(row);
    if (total === 0) return 0;
    return (Number(row?.orderTotals?.cancelled || 0) / total) * 100;
}
